import { 
  LayoutDashboard, 
  Home,
  ClipboardList, 
  Sliders, 
  Zap, 
  Shield, 
  Star, 
  QrCode, 
  User, 
  Settings, 
  Users, 
  Package, 
  Calendar,
  CalendarCheck,
  DollarSign, 
  Download,
  Workflow,
  Handshake,
  PieChart,
  Tag,
  Bell,
  Store,
  CreditCard,
  Sparkles,
  TrendingUp,
  Receipt,
  FileText,
  UserPlus,
  Target,
  Share2,
  Gift,
  FileBarChart
} from "lucide-react";

/**
 * Navigation structure for DashboardV2
 * SINGLE SOURCE OF TRUTH for sidebar tabs
 * Used by: DashboardV2, RolesTab (for permission checkboxes)
 */
export const navigationGroups = [
  {
    id: 'main',
    label: 'Bridge',
    icon: LayoutDashboard,
    items: [
      { id: 'overview', label: 'Manifest', icon: Home, alwaysVisible: true },
      { id: 'schedule', label: 'Calendar', icon: Calendar },
      { id: 'mySchedule', label: 'Schedule', icon: CalendarCheck },
      { id: 'home', label: 'Dashboard', icon: LayoutDashboard },
    ]
  },
  {
    id: 'operations',
    label: 'Deck',
    icon: Settings,
    items: [
      { id: 'roles', label: 'Roles', icon: Shield },
      { id: 'teamMembers', label: 'Team', icon: Users },
      { id: 'assets', label: 'Assets', icon: Package },
      { id: 'services', label: 'Services', icon: Zap },
      { id: 'flows', label: 'Flows', icon: Sparkles },
    ]
  },
  {
    id: 'financials',
    label: 'Purser',
    icon: DollarSign,
    items: [
      { id: 'payments', label: 'Payments', icon: CreditCard },
      { id: 'profitLoss', label: 'Profit & Loss', icon: PieChart },
      { id: 'revenue', label: 'Revenue', icon: TrendingUp },
      { id: 'expenses', label: 'Expenses', icon: Receipt },
      { id: 'reports', label: 'Reports', icon: FileBarChart },
      { id: 'partners', label: 'Partners', icon: UserPlus },
    ]
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: ClipboardList,
    items: [
      { id: 'branding', label: 'Branding', icon: Sliders },
      { id: 'waivers', label: 'Waivers', icon: Shield },
      { id: 'reviews', label: 'Reviews', icon: Star },
      { id: 'checkin', label: 'Check-In', icon: QrCode },
      { id: 'relations', label: 'Relations', icon: User },
      { id: 'discounts', label: 'Discounts', icon: Tag },
      { id: 'giftCards', label: 'Gift Cards', icon: Gift },
    ]
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Store,
    items: [
      { id: 'marketplace', label: 'Marketplace', icon: Store },
      { id: 'blog', label: 'SEO', icon: FileText },
      // TEMP-HIDE (Jun 2026): Advertising tab parked for now — re-enable by uncommenting.
      // { id: 'advertising', label: 'Advertising', icon: Zap },
      { id: 'conversions', label: 'Conversions', icon: Target },
      // TEMP-HIDE (Jun 2026): Distribution tab not ready — re-enable by uncommenting.
      // { id: 'distribution', label: 'Distribution', icon: Share2 },
      { id: 'notifications', label: 'Notifications', icon: Bell },
    ]
  }
];

/**
 * Get all tab IDs as flat array (for role permissions)
 */
export const getAllTabIds = () => {
  const tabs = [];
  
  navigationGroups.forEach(group => {
    if (group.isStandalone) {
      tabs.push({ id: group.id, label: group.label, group: null });
    } else if (group.items) {
      group.items.forEach(item => {
        tabs.push({ id: item.id, label: item.label, group: group.label });
      });
    }
  });
  
  return tabs;
};

/**
 * Filter navigation based on role permissions
 * @param {Object} tabPermissions - Object like { branding: { read: true, create: false }, waivers: { read: true } }
 * @param {Boolean} isAdmin - If true, show all tabs
 */
export const filterNavigationByPermissions = (tabPermissions = {}, isAdmin = false) => {
  if (isAdmin) {
    return navigationGroups; // Admins see everything
  }
  
  const filtered = navigationGroups.map(group => {
    if (group.isStandalone) {
      // Always-visible standalone tabs (e.g. the Home landing) bypass permission gating
      if (group.alwaysVisible) return group;
      // Check if user has permission for standalone item (like 'home')
      const permission = tabPermissions[group.id];
      const hasAccess = permission?.read === true || permission === true;
      return hasAccess ? group : null;
    }
    
    // Filter items based on permissions - check if user has READ access
    const filteredItems = group.items.filter(item => {
      // Always-visible items (e.g. the Manifest landing) bypass permission gating
      if (item.alwaysVisible) return true;
      const permission = tabPermissions[item.id];
      // Permission can be an object { read: true, ... } or a boolean for legacy support
      let hasAccess = false;
      if (typeof permission === 'object') {
        hasAccess = permission?.read === true;
      } else {
        hasAccess = permission === true;
      }
      return hasAccess;
    });
    
    return {
      ...group,
      items: filteredItems
    };
  }).filter(group => {
    // Remove null groups (standalone without permission)
    if (group === null) return false;
    // Remove groups with no items
    if (group.items && group.items.length === 0) return false;
    return true;
  });
  
  return filtered;
};
